import type { RobotState, RobotSummary } from '@fabricos/protocol-ts';
import { fmtNum } from '@/lib/utils';
import { cn } from '@/lib/utils';

/**
 * Five small sensor cards arranged in a 2-column grid: IMU (attitude +
 * placeholder for gyro/accel raw), Battery, GPS, Compass, Firmware. Each
 * card reads what the existing decoders give us and surfaces what isn't
 * available yet so the next protocol pass knows where to extend.
 */
export function SensorPanels({
  robot,
  state,
}: {
  robot: RobotSummary;
  state?: RobotState;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      <ImuCard state={state} />
      <BatteryCard state={state} />
      <GpsCard state={state} />
      <CompassCard state={state} />
      <FirmwareCard robot={robot} state={state} />
      <LinkCard state={state} />
    </div>
  );
}

function Card({ title, children, tone = 'default' }: { title: string; children: React.ReactNode; tone?: 'default' | 'warn' | 'critical' }) {
  return (
    <div
      className={cn(
        'rounded border bg-surface-1 p-3',
        tone === 'default' && 'border-border-default',
        tone === 'warn' && 'border-status-warn/40',
        tone === 'critical' && 'border-status-critical/40',
      )}
    >
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, unit }: { label: string; value: React.ReactNode; unit?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5 font-mono text-[11px]">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg-primary">
        {value}
        {unit && <span className="ml-0.5 text-[9px] text-fg-muted">{unit}</span>}
      </span>
    </div>
  );
}

function ImuCard({ state }: { state?: RobotState }) {
  const a = state?.attitude;
  const v = state?.health?.vibration;
  const clip = state?.health?.vibration_clipping_total;
  const vibPeak = v ? Math.max(v.x, v.y, v.z) : 0;
  const vibTone: 'default' | 'warn' | 'critical' = v
    ? vibPeak > 60
      ? 'critical'
      : vibPeak > 30
        ? 'warn'
        : 'default'
    : 'default';
  return (
    <Card title="IMU · attitude" tone={vibTone}>
      <Row label="roll" value={a ? fmtDeg(a.roll_rad) : '—'} unit="°" />
      <Row label="pitch" value={a ? fmtDeg(a.pitch_rad) : '—'} unit="°" />
      <Row label="yaw" value={a ? fmtDeg(a.yaw_rad, true) : '—'} unit="°" />
      <Row label="vib X" value={v ? v.x.toFixed(1) : '—'} unit="m/s²" />
      <Row label="vib Y" value={v ? v.y.toFixed(1) : '—'} unit="m/s²" />
      <Row label="vib Z" value={v ? v.z.toFixed(1) : '—'} unit="m/s²" />
      <Row label="clipping" value={clip != null ? clip : '—'} />
    </Card>
  );
}

function BatteryCard({ state }: { state?: RobotState }) {
  const b = state?.battery;
  if (!b) {
    return (
      <Card title="Battery">
        <div className="text-[11px] text-fg-muted">no telemetry</div>
      </Card>
    );
  }
  const pct = b.remaining_pct ?? null;
  const tone: 'default' | 'warn' | 'critical' = pct == null ? 'default' : pct < 20 ? 'critical' : pct < 30 ? 'warn' : 'default';
  return (
    <Card title="Battery" tone={tone}>
      <Row label="voltage" value={fmtNum(b.voltage_v, 2)} unit="V" />
      <Row label="current" value={b.current_a != null ? fmtNum(b.current_a, 1) : '—'} unit="A" />
      <Row label="remaining" value={pct != null ? `${Math.round(pct)}` : '—'} unit="%" />
      <Row label="used" value={b.capacity_used_mah != null ? Math.round(b.capacity_used_mah) : '—'} unit="mAh" />
      <Row
        label="cells"
        value={
          b.cells != null
            ? `${b.cells} · ${(b.voltage_v / b.cells).toFixed(2)} V/cell`
            : <span className="text-fg-muted">unknown</span>
        }
      />
    </Card>
  );
}

function GpsCard({ state }: { state?: RobotState }) {
  const g = state?.gps;
  if (!g) {
    return (
      <Card title="GPS">
        <div className="text-[11px] text-fg-muted">no GPS_RAW_INT yet</div>
      </Card>
    );
  }
  const tone: 'default' | 'warn' | 'critical' =
    g.fix === 'none' || g.fix === 'fix2d' ? 'critical' : g.satellites < 6 ? 'warn' : 'default';
  return (
    <Card title="GPS" tone={tone}>
      <Row label="fix" value={g.fix.toUpperCase()} />
      <Row label="sats" value={g.satellites} />
      <Row label="HDOP" value={g.hdop != null ? fmtNum(g.hdop, 2) : '—'} />
      <Row label="VDOP" value={g.vdop != null ? fmtNum(g.vdop, 2) : '—'} />
    </Card>
  );
}

function CompassCard({ state }: { state?: RobotState }) {
  const y = state?.attitude?.yaw_rad;
  const heading = y != null ? (((y * 180) / Math.PI + 360) % 360) : null;
  return (
    <Card title="Compass">
      <Row label="heading" value={heading != null ? heading.toFixed(0) : '—'} unit="°" />
      <Row
        label="calibration"
        value={<span className="text-fg-muted">protocol gap · MAG_CAL_STATUS not decoded</span>}
      />
      <Row
        label="declination"
        value={<span className="text-fg-muted">read COMPASS_DEC param (write-side)</span>}
      />
    </Card>
  );
}

function FirmwareCard({ robot, state }: { robot: RobotSummary; state?: RobotState }) {
  const firmware = robot.kind.firmware;
  const platform = (robot.kind.frame as { platform?: string }).platform ?? '—';
  const fw = state?.firmware;
  return (
    <Card title="Firmware">
      <Row label="autopilot" value={firmware.toUpperCase()} />
      <Row label="frame" value={platform} />
      <Row label="powertrain" value={robot.kind.powertrain} />
      <Row label="flight SW" value={fw?.flight_sw ?? '—'} />
      {fw?.middleware_sw && <Row label="middleware" value={fw.middleware_sw} />}
      {fw?.os_sw && <Row label="OS" value={fw.os_sw} />}
      {fw && (
        <Row
          label="board"
          value={`v${fw.board_version} · vendor ${fw.vendor_id} · product ${fw.product_id}`}
        />
      )}
      {fw && fw.uid > 0 && <Row label="UID" value={fw.uid.toString(16)} />}
    </Card>
  );
}

function LinkCard({ state }: { state?: RobotState }) {
  const l = state?.link;
  if (!l) {
    return (
      <Card title="Link health">
        <div className="text-[11px] text-fg-muted">no RADIO_STATUS yet</div>
      </Card>
    );
  }
  return (
    <Card title="Link health">
      <Row label="RSSI" value={l.rssi_dbm != null ? l.rssi_dbm : '—'} unit="dBm" />
      <Row label="latency" value={l.latency_ms != null ? l.latency_ms : '—'} unit="ms" />
      <Row label="packet loss" value={l.packet_loss_pct != null ? fmtNum(l.packet_loss_pct, 1) : '—'} unit="%" />
    </Card>
  );
}

function fmtDeg(rad: number, normalize = false): string {
  let deg = (rad * 180) / Math.PI;
  if (normalize) deg = ((deg % 360) + 360) % 360;
  return deg.toFixed(1);
}
